import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { PermissionVocabularyViewModel } from '../api';

/**
 * Read-only reference of the permission grammar (PTV2-1443): every slot with its allowed values, and
 * the canonical shapes. A lookup for admins composing roles — driven entirely by the vocabulary
 * endpoint, so it stays in step with the backend enums with nothing hardcoded.
 */
export function PermissionVocabularyReference({
  vocabulary,
}: {
  readonly vocabulary: PermissionVocabularyViewModel;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="space-y-4 text-sm">
      <section className="space-y-2">
        <h4 className="font-medium text-foreground">
          {t('routes.platform.detail.roles.permissions.reference.shapes')}
        </h4>
        <ul className="space-y-1">
          {vocabulary.shapes.map((shape) => (
            <li key={shape.id} className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-foreground">{shape.label}</span>
              <code className="text-xs text-muted-foreground">{shape.template}</code>
            </li>
          ))}
        </ul>
      </section>
      <section className="space-y-2">
        <h4 className="font-medium text-foreground">
          {t('routes.platform.detail.roles.permissions.reference.slots')}
        </h4>
        <dl className="space-y-2">
          {vocabulary.slots.map((slot) => (
            <div key={slot.name} className="grid grid-cols-[8rem_1fr] gap-2">
              <dt className="text-muted-foreground">{slot.label}</dt>
              <dd className="flex flex-wrap gap-1">
                {slot.values.map((term) => (
                  <code
                    key={term.value}
                    className="rounded bg-muted px-1.5 py-0.5 text-xs text-foreground"
                    title={term.value}
                  >
                    {term.label}
                  </code>
                ))}
              </dd>
            </div>
          ))}
        </dl>
      </section>
    </div>
  );
}
